import React, { useState } from 'react';
import { Edit, Trash2, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const forumCategories = [
  { id: 'applications', name: 'Applications' },
  { id: 'products', name: 'Products & Resources' },
  { id: 'suggested', name: 'Suggested New Apps/Products' },
  { id: 'blog', name: 'Blog Submissions & Comments' },
  { id: 'legal', name: 'Legal & Policy' },
  { id: 'governance', name: 'Governance' },
  { id: 'support', name: 'Support & Feedback' },
];

export const AdminForum = ({ forumTopics, loadData }) => {
  const [showEditForumModal, setShowEditForumModal] = useState(false);
  const [editingForum, setEditingForum] = useState(null);

  const handleUpdateForumTopic = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/forum/topics/${editingForum.id}`, {
        title: editingForum.title, content: editingForum.content, category: editingForum.category, author: editingForum.author
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Forum topic updated successfully!');
      setShowEditForumModal(false);
      setEditingForum(null);
      loadData();
    } catch (error) { toast.error('Failed to update forum topic'); }
  };

  const handleDeleteForumTopic = async (id) => {
    if (!window.confirm('Are you sure you want to delete this forum topic?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/forum/topics/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Forum topic deleted');
      loadData();
    } catch (error) { toast.error('Failed to delete forum topic'); }
  };

  return (
    <>
      <div className="bg-white rounded-xl p-8 shadow-sm border border-slate-200">
        <h2 className="text-2xl font-bold text-slate-900 mb-6">Forum Topics</h2>
        <p className="text-slate-600 mb-6">Edit or delete forum topics. Changes will be reflected on the Forum page.</p>
        <div className="space-y-4 max-h-[600px] overflow-y-auto">
          {forumTopics.length === 0 ? <p className="text-slate-600 text-center py-8">No forum topics yet.</p> : forumTopics.map((topic) => (
            <div key={topic.id} className="p-4 border border-slate-200 rounded-lg hover:border-slate-300">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <h3 className="font-bold text-slate-900">{topic.title}</h3>
                  <p className="text-sm text-slate-600 mt-1 line-clamp-2">{topic.content?.substring(0, 150)}...</p>
                  <div className="flex items-center gap-4 mt-2">
                    <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded">{topic.category}</span>
                    <span className="text-xs text-slate-500">By {topic.author}</span>
                    <span className="text-xs text-slate-500">{topic.replies || 0} replies</span>
                    <span className="text-xs text-slate-500">{topic.views || 0} views</span>
                  </div>
                </div>
                <div className="flex gap-2 ml-4">
                  <button onClick={() => { setEditingForum({ id: topic.id, title: topic.title, content: topic.content, category: topic.category, author: topic.author }); setShowEditForumModal(true); }} className="text-blue-500 hover:text-blue-700"><Edit className="h-4 w-4" /></button>
                  <button onClick={() => handleDeleteForumTopic(topic.id)} className="text-red-500 hover:text-red-700"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {showEditForumModal && editingForum && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full p-8 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-slate-900">Edit Forum Topic</h3>
              <button onClick={() => { setShowEditForumModal(false); setEditingForum(null); }}><X className="h-6 w-6 text-slate-500" /></button>
            </div>
            <form onSubmit={handleUpdateForumTopic} className="space-y-4">
              <div><Label>Title</Label><Input value={editingForum.title} onChange={(e) => setEditingForum({ ...editingForum, title: e.target.value })} required className="mt-2" /></div>
              <div><Label>Category</Label>
                <select value={editingForum.category} onChange={(e) => setEditingForum({ ...editingForum, category: e.target.value })} className="w-full mt-2 px-3 py-2 border border-slate-300 rounded-lg">
                  {forumCategories.map(cat => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
                </select></div>
              <div><Label>Author</Label><Input value={editingForum.author} onChange={(e) => setEditingForum({ ...editingForum, author: e.target.value })} required className="mt-2" /></div>
              <div><Label>Content</Label><Textarea value={editingForum.content} onChange={(e) => setEditingForum({ ...editingForum, content: e.target.value })} required className="mt-2" rows={12} /></div>
              <div className="flex gap-3 pt-4">
                <Button type="submit" className="flex-1 bg-[#8B1818] hover:bg-[#701313] text-white"><Edit className="mr-2 h-4 w-4" />Update Forum Topic</Button>
                <Button type="button" variant="outline" onClick={() => { setShowEditForumModal(false); setEditingForum(null); }}>Cancel</Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};
